<!--
// app/components/pages/region-tables/MetaBracketPlayoff.vue
-->
<!--
  MetaBracketPlayoff.vue

  Renders playoff matches in a tournament bracket layout using API metadata.
  Supports multiple turns per column, automatic line connections, and team color styling.

  See: app/composables/bracket/README.md for full documentation
-->
<script setup lang="ts">
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import type { Match } from "~/src/match/domain/models/Match";
import type { Team } from "~/src/team/domain/models/Team";
import type {
  BracketColumn,
  BracketConfigMeta,
  TurnGroup,
} from "~/src/bracket-configs/domain/models/Bracket";
import { teamArrToObjById } from "~/src/team/domain/services/teamService";
import { setAliasUrl } from "~/src/Shared/utils";
import BracketMatchCard from "~/app/components/pages/region-tables/BracketMatchCard.vue";

type Props = {
  playoffPhase: Phase | undefined;
  seasonId: number | undefined;
  winnerTeamId: number | null;
  region: string;
  isHome?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  isHome: false,
});

const teams = ref<Record<number, Team> | undefined>();
const bracketColumns = ref<BracketColumn[]>([]);
const containerRef = ref<HTMLElement | null>(null);
const columnRefs = ref<(HTMLElement | null)[]>([]);
const matchRefs = ref<Map<string, HTMLElement>>(new Map());
const connectionLines = ref<any[]>([]);
const isMounted = ref(false);

const seasonTeams = computed(() => {
  if (!props.playoffPhase || !props.playoffPhase.groups.length) return undefined;
  return props.playoffPhase.groups[0].teams;
});

// Watch playoff phase and rebuild bracket structure when it changes
watch(
  () => props.playoffPhase,
  newPhase => {
    if (newPhase) {
      // Clear previous state to prevent memory leaks
      connectionLines.value = [];
      matchRefs.value.clear();
      buildBracketFromMeta(newPhase);

      // Recalculate connection lines after DOM updates
      if (isMounted.value) {
        nextTick(() => {
          // Use requestAnimationFrame to ensure DOM is fully rendered
          requestAnimationFrame(() => {
            calculateLines();
          });
        });
      }
    }
  },
  { immediate: true }
);

// Load team data for display
watch(
  seasonTeams,
  async newTeams => {
    if (newTeams) {
      const theTeams = await teamArrToObjById(newTeams);
      teams.value = theTeams || undefined;
    }
  },
  { immediate: true }
);

// Lifecycle: calculate lines once DOM is mounted
onMounted(() => {
  isMounted.value = true;
  nextTick(() => {
    requestAnimationFrame(() => {
      calculateLines();
    });
  });
});

onBeforeUnmount(() => {
  isMounted.value = false;
  matchRefs.value.clear();
  connectionLines.value = [];
});

/**
 * Builds bracket column structure from phase metadata
 * Parses bracket_config from each match's metaInformation
 * Groups matches by column and turn, then sorts by position
 */
function buildBracketFromMeta(phase: Phase) {
  const matchesWithConfig: Array<{ match: Match; config: BracketConfigMeta }> = [];

  // Extract all matches that have bracket_config in metaInformation
  for (const group of phase.groups) {
    for (const turn of group.turns) {
      for (const match of turn.matches) {
        const bracketConfigStr = match.metaInformation?.bracket_config;
        if (bracketConfigStr) {
          try {
            const config = JSON.parse(bracketConfigStr) as BracketConfigMeta;
            matchesWithConfig.push({ match, config });
          } catch (error) {
            console.warn(`Failed to parse bracket_config for match ${match.id}:`, error);
          }
        }
      }
    }
  }

  // Group matches by bracket_column, then by turn_name
  const columnsMap = new Map<number, Map<string, TurnGroup>>();

  matchesWithConfig.forEach(({ match, config }) => {
    // Find the original turn to use as fallback for turn_name
    let originalTurnName = config.turn_name || "";
    if (!originalTurnName) {
      // Search for the turn that contains this match
      for (const group of phase.groups) {
        for (const turn of group.turns) {
          if (turn.matches.some(m => m.id === match.id)) {
            originalTurnName = turn.name;
            break;
          }
        }
        if (originalTurnName) break;
      }
    }

    const effectiveTurnName = originalTurnName;

    // Initialize column map if it doesn't exist
    if (!columnsMap.has(config.bracket_column)) {
      columnsMap.set(config.bracket_column, new Map());
    }

    const turnsMap = columnsMap.get(config.bracket_column)!;

    // Initialize turn group if it doesn't exist
    if (!turnsMap.has(effectiveTurnName)) {
      turnsMap.set(effectiveTurnName, {
        turn_name: effectiveTurnName,
        matches: [],
      });
    }

    turnsMap.get(effectiveTurnName)!.matches.push({ match, config });
  });

  // Convert maps to arrays, sort columns by index, and sort matches within turns
  const columns = Array.from(columnsMap.entries())
    .sort(([a], [b]) => a - b)
    .map(([columnIndex, turnsMap]) => {
      const turns = Array.from(turnsMap.values()).map(turn => ({
        ...turn,
        matches: turn.matches.sort((a, b) => a.config.position_in_turn - b.config.position_in_turn),
      }));

      return {
        columnIndex,
        turns,
      };
    });

  bracketColumns.value = columns;
}

/**
 * Finds the column index for a given match ID
 */
function findMatchColumn(matchId: number): number | null {
  for (let colIndex = 0; colIndex < bracketColumns.value.length; colIndex++) {
    const column = bracketColumns.value[colIndex];
    for (const turn of column.turns) {
      if (turn.matches.some(m => m.match.id === matchId)) {
        return colIndex;
      }
    }
  }
  return null;
}

/**
 * Categorizes a connection as intra-column, adjacent-column, or skip-column
 */
function categorizeConnection(fromMatchId: number, toMatchId: number) {
  const fromCol = findMatchColumn(fromMatchId);
  const toCol = findMatchColumn(toMatchId);

  if (fromCol === null || toCol === null) {
    return { type: "adjacent-column" as const, fromColumn: fromCol, toColumn: toCol, columnsSkipped: 0 };
  }

  const columnDiff = Math.abs(toCol - fromCol);

  if (fromCol === toCol) {
    return { type: "intra-column" as const, fromColumn: fromCol, toColumn: toCol, columnsSkipped: 0 };
  } else if (columnDiff === 1) {
    return { type: "adjacent-column" as const, fromColumn: fromCol, toColumn: toCol, columnsSkipped: 0 };
  } else {
    return {
      type: "skip-column" as const,
      fromColumn: fromCol,
      toColumn: toCol,
      columnsSkipped: columnDiff - 1,
    };
  }
}

/**
 * Calculates a proportional connection point along the match box edge
 */
function calculateProportionalPoint(
  matchEl: HTMLElement,
  totalPoints: number,
  pointIndex: number,
  isExit: boolean,
  isRTL: boolean,
  totalEntriesOnSide: number = 0,
  totalExitsOnSide: number = 0
) {
  if (!containerRef.value) return null;

  const containerRect = containerRef.value.getBoundingClientRect();
  const matchRect = matchEl.getBoundingClientRect();

  // Calculate Y position
  let y;

  // Special cases for centering connections:
  // 1. Exactly 2 entries (regardless of exits) - keep current behavior
  const shouldOverlap2Entries = !isExit && totalEntriesOnSide === 2;

  // 2. Only 1 connection on this side (entry OR exit)
  const hasSingleConnectionOnSide = totalPoints === 1;

  // 3. Match has 1 entry AND 1 exit (on different sides)
  const hasSingleEntryAndExit = totalEntriesOnSide === 1 && totalExitsOnSide === 1;

  if (hasSingleConnectionOnSide || shouldOverlap2Entries || hasSingleEntryAndExit) {
    // Center connection
    y = matchRect.top + matchRect.height / 2 - containerRect.top;
  } else {
    // Multiple connections: distribute proportionally along height
    const segment = matchRect.height / (totalPoints + 1);
    y = matchRect.top + segment * (pointIndex + 1) - containerRect.top;
  }

  // Calculate X position based on exit/entry and RTL
  let x;
  if (isExit) {
    // Exit from right (LTR) or left (RTL)
    x = isRTL ? matchRect.left - containerRect.left : matchRect.right - containerRect.left;
  } else {
    // Entry from left (LTR) or right (RTL)
    x = isRTL ? matchRect.right - containerRect.left : matchRect.left - containerRect.left;
  }

  return { x, y };
}

/**
 * Preprocesses all connections to count exits and entries per match
 * Also calculates proper ordering: entries first (top), then exits (bottom)
 */
function preprocessConnections() {
  const exitCounts = new Map<number, number>();
  const entryCounts = new Map<number, number>();
  const entryIndexMap = new Map<string, number>(); // key: "toMatchId-fromMatchId" → entryIndex
  const sideConnectionCounts = new Map<number, number>(); // Total connections on same side per match

  // First pass: count all connections
  bracketColumns.value.forEach(column => {
    column.turns.forEach(turn => {
      turn.matches.forEach(matchData => {
        if (!matchData.config.connections || matchData.config.connections.length === 0) {
          return;
        }

        // Count exits from this match
        const exitCount = matchData.config.connections.length;
        exitCounts.set(matchData.match.id, exitCount);

        // Count entries to destination matches
        matchData.config.connections.forEach(connection => {
          const currentCount = entryCounts.get(connection.to_match_id) || 0;
          entryCounts.set(connection.to_match_id, currentCount + 1);

          // Map entry index for this specific connection (will adjust later for ordering)
          const key = `${connection.to_match_id}-${matchData.match.id}`;
          entryIndexMap.set(key, currentCount);
        });
      });
    });
  });

  // Second pass: calculate total connections per side for each match
  // This includes both entries and exits on the same side (for intra-column and inter-column)
  bracketColumns.value.forEach(column => {
    column.turns.forEach(turn => {
      turn.matches.forEach(matchData => {
        const matchId = matchData.match.id;
        const exits = exitCounts.get(matchId) || 0;
        const entries = entryCounts.get(matchId) || 0;

        // For adjacent/intra-column: entries and exits share the same side
        // Total on that side = entries + exits
        const totalOnSide = entries + exits;
        sideConnectionCounts.set(matchId, totalOnSide);
      });
    });
  });

  // Third pass: adjust entry indices to be ordered BEFORE exits
  // Entries get indices 0, 1, 2... (top positions)
  // Exits get indices starting after entries (bottom positions)
  entryIndexMap.forEach((originalIndex, key) => {
    const [toMatchIdStr] = key.split("-");
    const toMatchId = parseInt(toMatchIdStr);
    const entriesCount = entryCounts.get(toMatchId) || 0;

    // Entry index stays as-is (0, 1, 2... from top)
    // This is already correct from first pass
  });

  return { exitCounts, entryCounts, entryIndexMap, sideConnectionCounts };
}

/**
 * Gets coordinates for intra-column connections (same column)
 */
function getIntraColumnCoordinates(
  fromMatchId: number,
  toMatchId: number,
  connectionIndex: number,
  totalExits: number,
  entryIndex: number,
  totalEntries: number,
  totalFromSide: number,
  totalToSide: number
) {
  if (!containerRef.value) return null;

  const fromEl = matchRefs.value.get(`match-${fromMatchId}`);
  const toEl = matchRefs.value.get(`match-${toMatchId}`);
  if (!fromEl || !toEl) return null;

  const isRTL = document.documentElement.dir === "rtl";

  // For FROM match: exits come AFTER entries
  // exitPointIndex = totalEntries + connectionIndex
  const exitPointIndex = totalFromSide - totalExits + connectionIndex;
  const fromEntries = totalFromSide - totalExits;
  const exitPoint = calculateProportionalPoint(
    fromEl,
    totalFromSide,
    exitPointIndex,
    true,
    isRTL,
    fromEntries,
    totalExits
  );
  if (!exitPoint) return null;

  // For TO match: entries come FIRST (before exits)
  // entryIndex is already 0, 1, 2... which is correct
  const toExits = totalToSide - totalEntries;
  const entryPoint = calculateProportionalPoint(
    toEl,
    totalToSide,
    entryIndex,
    true,
    isRTL,
    totalEntries,
    toExits
  );
  if (!entryPoint) return null;

  return {
    x1: exitPoint.x,
    y1: exitPoint.y,
    x2: entryPoint.x,
    y2: entryPoint.y,
    type: "intra-column" as const,
  };
}

/**
 * Gets coordinates for adjacent-column connections
 */
function getAdjacentColumnCoordinates(
  fromMatchId: number,
  toMatchId: number,
  connectionIndex: number,
  totalExits: number,
  entryIndex: number,
  totalEntries: number,
  totalFromSide: number,
  totalToSide: number
) {
  if (!containerRef.value) return null;

  const fromEl = matchRefs.value.get(`match-${fromMatchId}`);
  const toEl = matchRefs.value.get(`match-${toMatchId}`);
  if (!fromEl || !toEl) return null;

  const isRTL = document.documentElement.dir === "rtl";

  // For FROM match: exits come AFTER entries (on right/left side)
  const exitPointIndex = totalFromSide - totalExits + connectionIndex;
  const fromEntries = totalFromSide - totalExits;
  const exitPoint = calculateProportionalPoint(
    fromEl,
    totalFromSide,
    exitPointIndex,
    true,
    isRTL,
    fromEntries,
    totalExits
  );
  if (!exitPoint) return null;

  // For TO match: entries come FIRST (on opposite side - left/right)
  const toExits = totalToSide - totalEntries;
  const entryPoint = calculateProportionalPoint(
    toEl,
    totalToSide,
    entryIndex,
    false,
    isRTL,
    totalEntries,
    toExits
  );
  if (!entryPoint) return null;

  return {
    x1: exitPoint.x,
    y1: exitPoint.y,
    x2: entryPoint.x,
    y2: entryPoint.y,
    type: "adjacent-column" as const,
  };
}

/**
 * Gets coordinates for skip-column connections (from bottom to top)
 */
function getSkipColumnCoordinates(
  fromMatchId: number,
  toMatchId: number,
  connectionIndex: number,
  totalExits: number
) {
  if (!containerRef.value) return null;

  const fromEl = matchRefs.value.get(`match-${fromMatchId}`);
  const toEl = matchRefs.value.get(`match-${toMatchId}`);
  if (!fromEl || !toEl) return null;

  const containerRect = containerRef.value.getBoundingClientRect();
  const fromRect = fromEl.getBoundingClientRect();
  const toRect = toEl.getBoundingClientRect();

  // Exit from center bottom with offset for multiple connections
  const x1 = fromRect.left + fromRect.width / 2 - containerRect.left;
  const y1 = fromRect.bottom - containerRect.top;

  // Entry at center top
  const x2 = toRect.left + toRect.width / 2 - containerRect.left;
  const y2 = toRect.top - containerRect.top;

  // Calculate vertical offset for this connection
  const downOffset = connectionIndex * 15; // 0px, 15px, 30px...

  return {
    x1,
    y1,
    x2,
    y2,
    downOffset,
    type: "skip-column" as const,
  };
}

/**
 * Calculates all connection lines between matches
 * Uses manual calculation (not reactive) to avoid performance issues
 * Generates specific line types based on connection category
 */
function calculateLines() {
  if (!isMounted.value || !bracketColumns.value.length || !containerRef.value || matchRefs.value.size === 0) {
    connectionLines.value = [];
    return;
  }

  const lines: any[] = [];
  const isRTL = document.documentElement.dir === "rtl";

  // Preprocess to count exits and entries
  const { exitCounts, entryCounts, entryIndexMap, sideConnectionCounts } = preprocessConnections();

  // Process each connection individually
  bracketColumns.value.forEach(column => {
    column.turns.forEach(turn => {
      turn.matches.forEach(matchData => {
        if (!matchData.config.connections || matchData.config.connections.length === 0) {
          return;
        }

        matchData.config.connections.forEach((connection, connectionIndex) => {
          // Categorize connection
          const category = categorizeConnection(matchData.match.id, connection.to_match_id);

          // Get counts for proportional distribution
          const totalExits = exitCounts.get(matchData.match.id) || 0;
          const totalEntries = entryCounts.get(connection.to_match_id) || 0;
          const entryIndex = entryIndexMap.get(`${connection.to_match_id}-${matchData.match.id}`) || 0;

          // Get total connections on same side (for ordering)
          const totalFromSide = sideConnectionCounts.get(matchData.match.id) || 1;
          const totalToSide = sideConnectionCounts.get(connection.to_match_id) || 1;

          // Get coordinates based on connection type
          let coords;
          if (category.type === "intra-column") {
            coords = getIntraColumnCoordinates(
              matchData.match.id,
              connection.to_match_id,
              connectionIndex,
              totalExits,
              entryIndex,
              totalEntries,
              totalFromSide,
              totalToSide
            );
          } else if (category.type === "skip-column") {
            coords = getSkipColumnCoordinates(
              matchData.match.id,
              connection.to_match_id,
              connectionIndex,
              totalExits
            );
          } else {
            // adjacent-column
            coords = getAdjacentColumnCoordinates(
              matchData.match.id,
              connection.to_match_id,
              connectionIndex,
              totalExits,
              entryIndex,
              totalEntries,
              totalFromSide,
              totalToSide
            );
          }

          if (!coords) return;

          // Generate line segments based on type
          if (category.type === "intra-column") {
            // C-shape: horizontal out, vertical, horizontal in (NARROW)
            const offset = 15; // Reduced from 25 to make C narrower
            const verticalX = isRTL ? coords.x1 - offset : coords.x1 + offset;

            lines.push(
              { type: "line", x1: coords.x1, y1: coords.y1, x2: verticalX, y2: coords.y1 },
              { type: "line", x1: verticalX, y1: coords.y1, x2: verticalX, y2: coords.y2 },
              { type: "line", x1: verticalX, y1: coords.y2, x2: coords.x2, y2: coords.y2 }
            );
          } else if (category.type === "skip-column") {
            // U-shape: down, horizontal, up (SHORTER initial vertical)
            const downDistance = 6 + (coords.downOffset || 0); // Reduced from 30
            const bottomY = coords.y1 + downDistance;

            lines.push(
              { type: "line", x1: coords.x1, y1: coords.y1, x2: coords.x1, y2: bottomY },
              { type: "line", x1: coords.x1, y1: bottomY, x2: coords.x2, y2: bottomY },
              { type: "line", x1: coords.x2, y1: bottomY, x2: coords.x2, y2: coords.y2 }
            );
          } else {
            // adjacent-column: T-shape (original behavior restored)
            const verticalX = coords.x1 + (coords.x2 - coords.x1) * 0.5;

            lines.push(
              { type: "line", x1: coords.x1, y1: coords.y1, x2: verticalX, y2: coords.y1 },
              { type: "line", x1: verticalX, y1: coords.y1, x2: verticalX, y2: coords.y2 },
              { type: "line", x1: verticalX, y1: coords.y2, x2: coords.x2, y2: coords.y2 }
            );
          }
        });
      });
    });
  });

  connectionLines.value = lines;
}
</script>

<template>
  <div v-if="bracketColumns.length && teams" :key="`season-${seasonId}`" class="mt-6 md:mt-8">
    <div v-if="!isHome" class="pb-2">
      <p class="kql-page-section-title">Playoff</p>
      <hr />
    </div>

    <div class="mb-4 md:text-center">
      <div class="max-w-full overflow-x-scroll hide-scrollbar px-4">
        <div ref="containerRef" class="relative mt-4 inline-flex gap-12 max-w-max mx-auto ps-2 min-h-[400px]">
          <!-- Columns -->
          <div
            v-for="(column, columnIndex) in bracketColumns"
            :key="`column-${columnIndex}`"
            :ref="
              el => {
                columnRefs[columnIndex] = el as HTMLElement | null;
              }
            "
            class="flex flex-col min-w-[210px]"
          >
            <!-- First Turn Title (always at top when multiple turns exist) -->
            <div v-if="column.turns.length > 1" class="flex flex-col mb-auto">
              <p class="font-bold text-lg md:text-xl text-center mb-2">
                {{ column.turns[0].turn_name }}
              </p>
            </div>

            <!-- Single Turn Title (at top when only one turn) -->
            <div v-else-if="column.turns.length === 1" class="flex flex-col mb-auto">
              <p class="font-bold text-lg md:text-xl text-center mb-2">
                {{ column.turns[0].turn_name }}
              </p>
            </div>

            <!-- Matches Section (centered vertically) -->
            <div class="flex flex-col justify-center flex-1">
              <template
                v-for="(turn, turnIndex) in column.turns"
                :key="`turn-matches-${columnIndex}-${turnIndex}`"
              >
                <!-- Turn separator for subsequent turns in same column -->
                <div :class="{ 'mt-16': turnIndex > 0 }">
                  <!-- Inline turn title for subsequent turns (not first) -->
                  <p
                    v-if="turnIndex > 0 && column.turns.length > 1"
                    class="font-bold text-lg md:text-xl text-center mb-4"
                  >
                    {{ turn.turn_name }}
                  </p>

                  <div class="flex flex-col gap-6">
                    <div
                      v-for="matchData in turn.matches"
                      :key="`match-${matchData.match.id}`"
                      :ref="
                        el => {
                          if (el) matchRefs.set(`match-${matchData.match.id}`, el as HTMLElement);
                        }
                      "
                      class="flex items-center"
                    >
                      <bracket-match-card :match="matchData.match" :teams="teams" :region="region" />
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- SVG Overlay for Connections -->
          <svg
            v-if="connectionLines.length > 0"
            class="absolute top-0 left-0 w-full h-full pointer-events-none"
            style="z-index: 1"
          >
            <line
              v-for="(line, index) in connectionLines"
              :key="`line-${index}`"
              :x1="line.x1"
              :y1="line.y1"
              :x2="line.x2"
              :y2="line.y2"
              class="stroke-primary"
              stroke-width="1"
            />
          </svg>
        </div>
      </div>
    </div>

    <!-- Winner Display -->
    <div v-if="winnerTeamId && !!teams[winnerTeamId]" class="mb-6 flex items-center gap-1 md:justify-center">
      {{ $t("team.historicPlayoff.winner") }}:
      <div class="flex items-center justify-start gap-0.5">
        <nuxt-img
          :src="setAliasUrl(teams[winnerTeamId].logo.url) || ''"
          :alt="teams[winnerTeamId].shortName || ''"
          class="max-w-11 h-11 object-contain"
          height="44"
          width="44"
          loading="lazy"
        />
        <span class="font-bold text-lg">{{ teams[winnerTeamId].completeName }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
